import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
} from '@mui/material'
import { useCreateCreditPayment } from '../../hooks/api/useCreditPayments'
import { Bill } from '../../types'
import { formatCurrency } from '../../utils/formatters'
import { billRemainingBalance } from '../../utils/billBalance'
import PaymentAccountPicker from '../ui/PaymentAccountPicker'

interface RecordPaymentModalProps {
  open: boolean
  onClose: () => void
  bill: Bill
}

const RecordPaymentModal = ({ open, onClose, bill }: RecordPaymentModalProps) => {
  const [amountPaid, setAmountPaid] = useState('')
  const [paymentDate, setPaymentDate] = useState('')
  const [remarks, setRemarks] = useState('')
  const [source, setSource] = useState('cash')
  const [amountError, setAmountError] = useState('')

  const createPayment = useCreateCreditPayment()

  const remainingBalance = billRemainingBalance(bill)

  const handleClose = () => {
    setAmountPaid('')
    setPaymentDate('')
    setRemarks('')
    setSource('cash')
    setAmountError('')
    onClose()
  }

  const handleAmountChange = (value: string) => {
    setAmountPaid(value)
    const num = parseFloat(value)
    if (isNaN(num) || num <= 0 || !/^\d+(\.\d{1,2})?$/.test(value)) {
      setAmountError('Amount must be greater than 0')
    } else if (num > remainingBalance + 0.001) {
      setAmountError(`Cannot exceed remaining balance of Rs ${formatCurrency(remainingBalance)}`)
    } else {
      setAmountError('')
    }
  }

  const handleSubmit = () => {
    const num = parseFloat(amountPaid)
    if (isNaN(num) || num <= 0 || amountError || !/^\d+(\.\d{1,2})?$/.test(amountPaid)) return

    createPayment.mutate(
      {
        bill_id: bill._id,
        amount_paid: num,
        ...(paymentDate && { payment_date: paymentDate }),
        ...(remarks && { remarks }),
        payment_account: source === 'cash' ? 'cash' : 'bank',
        ...(source !== 'cash' && { bank_account_id: source }),
      },
      { onSuccess: handleClose }
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record Payment</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary">Customer</Typography>
          <Typography variant="body1" fontWeight={500}>{bill.customer_name}</Typography>
        </Box>

        <Box display="flex" gap={4} mb={2}>
          <Box>
            <Typography variant="body2" color="text.secondary">Net Payable</Typography>
            <Typography variant="body1">Rs {formatCurrency(bill.summary.net_payable)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Already Collected</Typography>
            <Typography variant="body1">Rs {formatCurrency(bill.paid_amount ?? 0)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Sale Returns</Typography>
            <Typography variant="body1">Rs {formatCurrency(bill.returned_amount ?? 0)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Remaining Balance</Typography>
            <Typography variant="body1" color="error.main" fontWeight={600}>
              Rs {formatCurrency(remainingBalance)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" gap={1} alignItems="flex-start">
            <TextField
              label="Amount Paid"
              type="number"
              value={amountPaid}
              onChange={(e) => handleAmountChange(e.target.value)}
              error={!!amountError}
              helperText={amountError || `Max: Rs ${formatCurrency(remainingBalance)}`}
              fullWidth
              autoFocus
              inputProps={{ min: 0.01, step: 0.01 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleAmountChange(remainingBalance.toFixed(2))}
              sx={{ mt: 1, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Full Amount
            </Button>
          </Box>
          <TextField
            label="Payment Date"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Remarks (optional)"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <PaymentAccountPicker value={source} onChange={setSource} label="Received into" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createPayment.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!amountPaid || !!amountError || createPayment.isPending}
        >
          {createPayment.isPending ? 'Saving...' : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RecordPaymentModal
